import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Card, DatePicker, Descriptions, Input, Modal, Space, Spin, Tag, Typography, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { getRollbackSettlementQuote, getRollbackStayQuote, receiveRollbackRefund } from '../../../../../Actions'
import { Invoice, RollbackSettlementQuote, RollbackStayQuote } from '../../../../../types/refund'
import { quoteOrReason, QuoteResult } from '../../../../../utils/quoteError'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  open: boolean
  workspaceId: number
  invoice: Invoice | null
  /**
   * 되감을 전환 약정. **호출부가 서버 값(`InvoiceRefundView.rollbackSubscriptionId`)을 넘긴다**
   * — 결제 건에서 유도하지 않는다(aipark-front #269). 대상 지목의 주인은 서버다.
   */
  subscriptionId: number | null
  onClose: () => void
}

/** 고객이 무엇을 원하는지. 서버는 알 수 없고 담당자가 문의를 받아 고른다 (D-33) */
type Intent = 'STAY' | 'EXIT'

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)

/**
 * 되감기 의도 선택 + 접수 (화면 명세 3절 · aipark-front #266 P4)
 *
 * 전환을 무를 때 **고객이 구독을 계속하는지(잔류)·그만두는지(이탈)** 는 서버가 알 수 없다. 두
 * 산정은 재료가 같고 답이 다르다 — 잔류는 직전 약정을 복원만 하므로 돌려줄 돈이 전환 결제 한
 * 건이고, 이탈은 거기에 복원된 약정의 구독취소 정산이 더해진다. 그래서 두 금액을 나란히 놓고
 * 담당자가 고른다.
 *
 * 🔴 **자격 판정을 화면이 하지 않는다.** 서버 규칙이 6조건이라(`RollbackSettlementService.gather`)
 * 복제하면 두 곳이 갈린다. 진입 가부는 호출부가 서버의 `rollbackAvailable` 로 거르고(#269),
 * 그래도 여기서 400 이 오면 `reason` 을 원문 그대로 띄운다 — 다음에 무엇을 해야 하는지가 그
 * 문장 안에 들어 있고, 기준일을 바꿔 다시 부르면 답이 갈릴 수 있다(가부는 기준일에 걸린다).
 */
export default function RollbackIntentModal({ open, workspaceId, invoice, subscriptionId, onClose }: Props) {
  const [referenceDate, setReferenceDate] = useState<Dayjs>(dayjs())
  const [intent, setIntent] = useState<Intent | undefined>()
  const [memo, setMemo] = useState('')
  const invalidate = useInvalidateRefunds(workspaceId)

  const isoDate = referenceDate.format('YYYY-MM-DD')

  useEffect(() => {
    if (!open) return
    setReferenceDate(dayjs())
    setIntent(undefined)
    setMemo('')
  }, [open, invoice?.id])

  /**
   * 두 산정을 **함께** 부른다. 담당자가 고르기 전에 양쪽 금액이 다 보여야 고를 수 있고,
   * 한쪽만 먼저 띄우면 그 금액이 기준처럼 읽힌다.
   *
   * `gcTime: 0` — 모달을 닫았다 열었을 때 낡은 기준일 산정이 보이면 담당자가 그 금액으로 접수한다.
   */
  const { data: quotes, isFetching } = useQuery({
    queryKey: ['refund-quote', 'rollback', subscriptionId, isoDate],
    queryFn: async (): Promise<{
      stay: QuoteResult<RollbackStayQuote>
      settlement: QuoteResult<RollbackSettlementQuote>
    }> => {
      const [stay, settlement] = await Promise.all([
        quoteOrReason<RollbackStayQuote>(() => getRollbackStayQuote(subscriptionId!, isoDate)),
        quoteOrReason<RollbackSettlementQuote>(() => getRollbackSettlementQuote(subscriptionId!, isoDate)),
      ])
      return { stay, settlement }
    },
    enabled: open && subscriptionId != null,
    gcTime: 0,
    retry: false,
  })

  const stay = quotes?.stay
  const settlement = quotes?.settlement

  const stayData = stay?.ok ? stay.data : null
  const settlementData = settlement?.ok ? settlement.data : null
  /** 복원이 사람에게 넘기는 항목. 접수 확인 모달도 이 값을 읽으므로 위에서 꺼내 둔다 */
  const stayManual = stayData?.manualFollowUps ?? []

  const { mutateAsync: receive, isPending } = useMutation({
    mutationFn: (exit: boolean) =>
      receiveRollbackRefund({
        subscriptionId: subscriptionId!,
        referenceDate: isoDate,
        exit,
        memo: memo.trim() || undefined,
      }),
  })

  /**
   * 🔴 금액을 보내지 않는다. 서버가 접수 시점에 다시 산정해 행을 만들고, 이탈형은 전환 결제 건·
   * 직전 약정 결제 건 **2행**을 같은 묶음 번호로 채번한다.
   *
   * 손으로 넣으면 ①두 금액의 합이 총액과 어긋나고 ②어느 청구서에 붙일지를 잘못 고를 수 있으며
   * ③묶음 번호가 한 글자만 달라도 후속 조치가 아무 오류 없이 영영 실행되지 않는다.
   */
  const submit = async () => {
    if (!intent) return

    try {
      const result = await receive(intent === 'EXIT')
      message.success(
        `되감기를 접수했습니다. 사건 ${result.refundGroupNo} · ${result.rows.length}행 · 총 ${money(result.totalRefund)}`,
      )
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '되감기를 접수하지 못했습니다.')
      return
    }

    await invalidate()
    onClose()
  }

  const confirmAndSubmit = () => {
    const exit = intent === 'EXIT'
    const stale = !referenceDate.isSame(dayjs(), 'day')

    Modal.confirm({
      title: exit ? '이탈형으로 접수할까요?' : '잔류형으로 접수할까요?',
      width: 560,
      content: (
        <Space direction="vertical">
          <span>
            {exit
              ? '전환 결제 건과 직전 약정 결제 건 2행이 같은 사건으로 만들어집니다. 둘 다 처리 완료로 확정돼야 후속 조치가 실행됩니다.'
              : '전환 결제 건 1행이 만들어집니다. 직전 약정이 복원되고 구독은 계속됩니다.'}
          </span>
          {/* 접수 시점 산정이 행에 박히고 이후 움직이지 않는다 */}
          <span>접수하면 산정값이 고정되며 수정할 수 없습니다. 정정은 철회 후 재접수입니다.</span>
          {!exit && stayManual.length ? (
            <span>
              복원 후 <b>수기 확인 {stayManual.length}건</b>이 남습니다 — 위 카드의 항목을 확인하세요.
            </span>
          ) : null}
          {stale ? (
            <span>
              철회일이 오늘이 아닌 <b>{isoDate}</b> 입니다.
            </span>
          ) : null}
        </Space>
      ),
      okText: '접수',
      cancelText: '취소',
      onOk: submit,
    })
  }

  /** 한쪽만 200 이면 그쪽만 고를 수 있다. 둘 다 400 이면 금액 없이 이유만 남는다 */
  const renderQuote = (
    key: Intent,
    title: string,
    subtitle: string,
    result: QuoteResult<RollbackStayQuote | RollbackSettlementQuote> | undefined,
    body: React.ReactNode,
    /** 금액 아래에 덧붙는 경고. 산정이 끊긴 카드에는 띄우지 않는다 */
    notice?: React.ReactNode,
  ) => {
    const selectable = !!result?.ok
    return (
      <Card
        size="small"
        title={
          <Space>
            <span>{title}</span>
            {intent === key ? <Tag color="blue">선택됨</Tag> : null}
          </Space>
        }
        style={{
          flex: 1,
          minWidth: 260,
          borderColor: intent === key ? '#1677ff' : undefined,
          opacity: selectable ? 1 : 0.7,
        }}
        extra={
          <Button size="small" type={intent === key ? 'primary' : 'default'} disabled={!selectable} onClick={() => setIntent(key)}>
            이걸로
          </Button>
        }
      >
        <Typography.Text type="secondary">{subtitle}</Typography.Text>
        {result && !result.ok ? (
          // 400 은 실패가 아니라 "산정할 수 없다 + 이유" 다. 요약하지 않는다
          <Alert type="warning" showIcon style={{ marginTop: 8 }} message="이 경로로는 산정할 수 없습니다" description={result.reason} />
        ) : (
          <div style={{ marginTop: 8 }}>
            {body}
            {notice}
          </div>
        )}
      </Card>
    )
  }


  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={880}
      title="되감기 — 잔류·이탈 선택"
      footer={[
        <Button key="close" onClick={onClose}>
          닫기
        </Button>,
        <Button key="submit" type="primary" loading={isPending} disabled={!intent} onClick={confirmAndSubmit}>
          되감기 접수
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="고객이 구독을 계속하는지 그만두는지에 따라 금액이 다릅니다"
        description="서버는 이것을 알 수 없습니다. 문의 내용을 보고 담당자가 고릅니다. 이 화면은 고르기 전까지 아무것도 기록하지 않습니다."
      />

      <Space style={{ marginBottom: 12 }}>
        <span>철회 기준일</span>
        <DatePicker allowClear={false} value={referenceDate} onChange={(value) => value && setReferenceDate(value)} />
      </Space>

      <Spin spinning={isFetching}>
        <Space align="start" style={{ display: 'flex', marginBottom: 12 }} wrap>
          {renderQuote(
            'STAY',
            '잔류형 — 갈아탄 것만 무름',
            '직전 약정을 복원하고 구독은 계속됩니다. 전환 결제 한 건만 돌려줍니다.',
            stay,
            stayData ? (
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="전환 결제액">{money(stayData.conversionCharge)}</Descriptions.Item>
                <Descriptions.Item label="전환 후 사용분">{money(stayData.usedAmount)}</Descriptions.Item>
                <Descriptions.Item label="환불액">
                  <Typography.Text strong>{money(stayData.refundAmount)}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="복원될 약정">
                  {stayData.restoredPlanName ?? '-'} (구독 #{stayData.restoredSubscriptionId ?? '-'})
                </Descriptions.Item>
                <Descriptions.Item label="복원 앵커·종료일">
                  {stayData.restoredAnchor ?? '-'} · {stayData.restoredEndAt ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="산정 내역">{stayData.calculation ?? '-'}</Descriptions.Item>
              </Descriptions>
            ) : null,
            // 🔴 복원이 자동으로 끝내지 못하는 항목. **접수 전에** 보여야 하는 값이다 —
            // 종전에는 복원 실행 응답에만 있어서 돌이킬 수 없는 실행 뒤에 도착했다(aivatar #512).
            stayManual.length ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="복원 후 수기 확인이 필요합니다"
                description={
                  <Space direction="vertical" size={0}>
                    {stayManual.map((item) => (
                      <span key={item}>· {item}</span>
                    ))}
                    <Typography.Text type="secondary">
                      이 항목은 서버가 복원하지 않고 담당자에게 넘깁니다. 접수 전에 확인하세요.
                    </Typography.Text>
                  </Space>
                }
              />
            ) : null,
          )}

          {renderQuote(
            'EXIT',
            '이탈형 — 무르고 그만둠',
            '직전 약정을 복원한 뒤 구독을 종료합니다. 결제 건 2개로 분해해서 냅니다.',
            settlement,
            settlementData ? (
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="전환 결제 건에서">{money(settlementData.conversionRefund)}</Descriptions.Item>
                <Descriptions.Item label="직전 약정 결제 건에서">{money(settlementData.priorRefund)}</Descriptions.Item>
                <Descriptions.Item label="총 환불액">
                  <Typography.Text strong>{money(settlementData.totalRefund)}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="사용분">{money(settlementData.usedAmount)}</Descriptions.Item>
                <Descriptions.Item label="순부담 (검산)">{money(settlementData.netBurden)}</Descriptions.Item>
                <Descriptions.Item label="산정 내역">{settlementData.calculation ?? '-'}</Descriptions.Item>
              </Descriptions>
            ) : null,
          )}
        </Space>

        {/* 총액만으로는 실행할 수 없다 — 결제대행사가 결제 건 금액을 넘는 취소를 받지 않는다 */}
        {intent === 'EXIT' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="2행이 만들어집니다"
            description="각 금액을 해당 청구서에서 따로 취소하고, 두 행을 각각 처리 완료로 확정하세요. 둘 다 확정돼야 후속 조치(직전 약정 복원 후 구독 종료)가 실행됩니다."
          />
        ) : null}

        <Space direction="vertical" style={{ width: '100%' }}>
          <span>메모 (선택)</span>
          <Input.TextArea
            rows={2}
            maxLength={500}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="문의 경위나 고객이 잔류·이탈을 고른 근거를 적어두세요"
          />
        </Space>
      </Spin>
    </Modal>
  )
}
